import { Flex, Box, Link, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import type { IOrder } from '@/types';
import { NavLink } from 'react-router-dom';
import { QuotePriceAndExpireDate } from '@/components/shared';
import { formatDate } from '@/utils';

interface IQuoteCardProps {
  isExpired?: boolean;
  quote: IOrder | undefined;
}

export const QuoteCard: React.FC<IQuoteCardProps> = (props): JSX.Element => {
  const { i18n, t } = useTranslation();
  const { isExpired, quote } = props;

  let quoteCreatedAt = '';

  if (quote?.createdAt) {
    quoteCreatedAt = new Date(quote.createdAt).toLocaleDateString('it-IT').replaceAll('/', '.');
  }

  if (!quote) return <div></div>;

  return (
    <Flex
      alignItems={{ base: 'flex-start', md: 'center' }}
      bg="white"
      boxShadow="0 0 8px 2px rgba(53,60,79, 0.1)"
      color={isExpired ? 'brand.gray' : 'unset'}
      direction={{ base: 'column', md: 'row' }}
      gap={10}
      justifyContent="flex-start"
      p={8}
      w="full"
    >
      <Flex direction="column" gap={2} w={{ base: '100%', md: '25%' }}>
        <Text fontSize={{ base: '24px', md: '18px' }}>
          {t('ACCOUNT.QUOTES.QUOTE_CARD.QUOTE_TITLE')}
          <br />
          <Text as="span" fontSize="18px">
            {quote.id}
          </Text>
        </Text>
        <Text>
          {t('ACCOUNT.QUOTES.QUOTE_CARD.OF')} {quoteCreatedAt}
        </Text>
        {quote.id && !isExpired && (
          <NavLink to={`/details/${quote.id}`}>
            <Link as="span" color="brand.red.light">
              {t('ACCOUNT.QUOTES.QUOTE_CARD.SHOW_DETAILS')}
            </Link>
          </NavLink>
        )}
      </Flex>
      <Flex direction="column" w={{ base: '100%', md: '25%' }}>
        <Text color="brand.gray" textTransform="uppercase">
          {t('ACCOUNT.QUOTES.QUOTE_CARD.COLLECT')}
        </Text>
        <Text fontSize="h4" fontWeight="bold" textTransform="capitalize">
          {quote.auctionHouse.name}
        </Text>
        <Text>{quote.auctionHouse.streetAddress}</Text>
        <Text>
          {quote.auctionHouse.zipCode} {quote.auctionHouse.city} {quote.auctionHouse.province}
        </Text>
      </Flex>
      <Flex direction="column" w={{ base: '100%', md: '25%' }}>
        <Text color="brand.gray" textTransform="uppercase">
          {t('ACCOUNT.QUOTES.QUOTE_CARD.RECIPIENT')}
        </Text>
        <Text fontSize="h4" fontWeight="bold" textTransform="capitalize">
          {quote.recipient.name}
        </Text>
        <Text>{quote.recipient.streetAddress}</Text>
        <Text>
          {quote.recipient.zipCode} {quote.recipient.city} {quote.recipient.province}
        </Text>
      </Flex>
      <Box flexGrow="2" textAlign={{ base: 'left', md: 'right' }}>
        <QuotePriceAndExpireDate
          expireDate={t('QUOTE_DETAILS.EXPIRATION_DATE', {
            date: formatDate(quote.quoteExpiryDate, i18n.language),
            interpolation: { escapeValue: false },
          })}
          price={quote.total}
          quoteStatus={quote.status}
        />
      </Box>
    </Flex>
  );
};
